import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Stroke } from '@share-paint/shared';
import { DrawingCanvas } from '../components/DrawingCanvas';
import { useGame } from '../state/GameProvider';
import { Countdown } from '../components/Countdown';

const PERSIST_KEY = 'share-paint:identity';
const hasIdentity = () =>
  typeof localStorage !== 'undefined' && !!localStorage.getItem(PERSIST_KEY);

const CANVAS_W = 900;
const CANVAS_H = 600;
const FLUSH_MS = 80;
const CANVAS_BG = '#fafaf7';

type Tool = 'pen' | 'eraser';

// Paleta rápida optimizada para tablet/desktop: 12 colores curados + el
// selector libre para todo lo demás.
const SWATCHES = [
  '#111827', // ink
  '#ffffff', // papel
  '#ef4444', // rojo
  '#f97316', // naranja
  '#eab308', // amarillo
  '#22c55e', // verde
  '#06b6d4', // cian
  '#3b82f6', // azul
  '#8b5cf6', // violeta
  '#ec4899', // rosa
  '#a16207', // ocre
  '#52525b', // gris
];

export function Game() {
  const { code } = useParams<{ code: string }>();
  const nav = useNavigate();
  const { state, submitStrokes, requestSnapshot } = useGame();
  const [color, setColor] = useState('#111827');
  const [width, setWidth] = useState(4);
  const [tool, setTool] = useState<Tool>('pen');
  const [showCustom, setShowCustom] = useState(false);
  const bufferRef = useRef<Stroke[]>([]);
  const flushTimer = useRef<number | null>(null);

  // navegar al panel de votación / revelación cuando el server cambie de fase
  useEffect(() => {
    if (!code) return;
    if (state.status === 'VOTING') nav(`/vote/${code}`, { replace: true });
    else if (state.status === 'REVEAL') nav(`/reveal/${code}`, { replace: true });
  }, [state.status, code, nav]);

  // Si tras 2.5s seguimos sin sesión activa NI identidad → volver al inicio.
  useEffect(() => {
    const t = setTimeout(() => {
      if (!state.session && !state.activeDrawing && !hasIdentity()) nav('/', { replace: true });
    }, 2500);
    return () => clearTimeout(t);
  }, [state.session, state.activeDrawing, nav]);

  useEffect(() => {
    if (state.activeDrawing) requestSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.activeDrawing?.id]);

  const flush = () => {
    if (bufferRef.current.length === 0) return;
    const batch = bufferRef.current;
    bufferRef.current = [];
    submitStrokes(batch);
  };

  const scheduleFlush = () => {
    if (flushTimer.current) return;
    flushTimer.current = window.setTimeout(() => {
      flushTimer.current = null;
      flush();
    }, FLUSH_MS);
  };

  const onLocalStroke = (s: Stroke) => {
    bufferRef.current.push(s);
    scheduleFlush();
  };

  useEffect(
    () => () => {
      if (flushTimer.current) window.clearTimeout(flushTimer.current);
    },
    [],
  );

  if (!state.activeDrawing || !state.me || !state.session) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/60">
        Sincronizando partida…
      </div>
    );
  }

  const drawing = state.activeDrawing;
  const players = state.session.players;
  const prompt = state.session.prompt;

  // Color y grosor efectivos según la herramienta. La goma se modela como
  // un trazo del color del fondo con grosor >= 16 para cubrir bien.
  const effectiveColor = tool === 'eraser' ? CANVAS_BG : color;
  const effectiveWidth = tool === 'eraser' ? Math.max(width, 16) : width;

  const isSwatchActive = (hex: string) =>
    tool === 'pen' && color.toLowerCase() === hex.toLowerCase();

  const pickSwatch = (hex: string) => {
    setTool('pen');
    setColor(hex);
  };

  return (
    <main className="min-h-screen p-4 lg:p-8 flex flex-col gap-4 max-w-[1200px] mx-auto">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase text-white/40">
            Prompt · Ronda {state.roundIndex + 1}/{state.totalRounds}
          </p>
          <h2 className="text-2xl font-bold">{prompt}</h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm px-3 py-1 rounded-full bg-accent/20 text-accent border border-accent/30">
            🎨 Dibuja sobre lo que ves
          </span>
          <Countdown msLeft={state.msLeft} />
        </div>
      </header>

      <section className="flex flex-col lg:flex-row gap-4 flex-1">
        <div className="flex-1 card p-0 overflow-hidden flex items-center justify-center">
          <DrawingCanvas
            width={CANVAS_W}
            height={CANVAS_H}
            strokes={drawing.strokes}
            myUserId={state.me.id}
            players={players}
            myColor={effectiveColor}
            brushWidth={effectiveWidth}
            onLocalStroke={onLocalStroke}
          />
        </div>

        <aside className="card w-full lg:w-72 flex flex-col gap-4">
          <div>
            <h4 className="text-sm font-medium text-white/60 mb-2">Jugadores</h4>
            <ul className="space-y-1">
              {players.map((p) => {
                const isMine = p.id === state.me!.id;
                return (
                  <li key={p.id} className="flex items-center gap-2 text-sm rounded-lg px-2 py-1">
                    <span className="w-3 h-3 rounded-full" style={{ background: p.color }} />
                    <span className="flex-1 truncate">{p.username}{isMine && <span className="text-accent"> (tú)</span>}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* ─────────────── herramientas ─────────────── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-white/60">Herramientas</h4>
              <div className="flex gap-1">
                <button
                  onClick={() => setTool('pen')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                    tool === 'pen'
                      ? 'bg-accent text-white'
                      : 'bg-white/5 hover:bg-white/10 text-white/80'
                  }`}
                  title="Lápiz"
                  aria-label="Lápiz"
                  aria-pressed={tool === 'pen'}
                >
                  ✏️ Lápiz
                </button>
                <button
                  onClick={() => setTool('eraser')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                    tool === 'eraser'
                      ? 'bg-accent text-white'
                      : 'bg-white/5 hover:bg-white/10 text-white/80'
                  }`}
                  title="Goma de borrar"
                  aria-label="Goma de borrar"
                  aria-pressed={tool === 'eraser'}
                >
                  🧽 Borrar
                </button>
              </div>
            </div>

            {/* Paleta rápida */}
            <div className="grid grid-cols-6 gap-2">
              {SWATCHES.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  onClick={() => pickSwatch(hex)}
                  disabled={tool === 'eraser'}
                  className={`relative aspect-square rounded-lg border-2 transition ${
                    isSwatchActive(hex)
                      ? 'border-accent scale-105 shadow-md shadow-accent/30'
                      : 'border-white/10 hover:border-white/30'
                  } ${tool === 'eraser' ? 'opacity-40 cursor-not-allowed' : ''}`}
                  style={{ background: hex }}
                  aria-label={`Color ${hex}`}
                  title={hex}
                >
                  {isSwatchActive(hex) && (
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold mix-blend-difference text-white">
                      ●
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Selector libre */}
            <button
              type="button"
              onClick={() => {
                setShowCustom((v) => !v);
                setTool('pen');
              }}
              className="mt-2 w-full text-xs px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 flex items-center justify-between"
            >
              <span>🎨 Color personalizado</span>
              <span className="inline-block w-4 h-4 rounded border border-white/20" style={{ background: color }} />
            </button>
            {showCustom && (
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => {
                    setColor(e.target.value);
                    setTool('pen');
                  }}
                  className="h-10 w-12 rounded-lg bg-transparent border border-white/10 cursor-pointer"
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    if (/^#[0-9a-fA-F]{6}$/.test(v)) setColor(v);
                  }}
                  className="input text-xs py-1.5"
                  placeholder="#000000"
                />
              </div>
            )}

            <div className="mt-3">
              <label className="text-xs text-white/40 flex justify-between">
                <span>Grosor</span>
                <span className="font-mono">{width}px</span>
              </label>
              <input
                type="range"
                min={1}
                max={20}
                value={width}
                onChange={(e) => setWidth(parseInt(e.target.value))}
                className="w-full"
              />
              {tool === 'eraser' && (
                <p className="mt-1 text-[10px] text-white/40 italic">
                  La goma usa grosor mínimo 16px para cubrir trazos.
                </p>
              )}
            </div>
          </div>

          <div className="mt-auto text-xs text-white/40 space-y-1">
            <div>· {drawing.version} v · {drawing.strokes.length} trazos en este lienzo</div>
            <div className="italic">
              Cuando pase el tiempo, la imagen actual pasará a otro jugador y recibirás otra.
            </div>
          </div>
        </aside>
      </section>
    </main>
  );
}
